import { useState } from 'react';
import { CheckExpanded } from './CheckExpanded.jsx';
import { severityFromCheck, severityClass } from '../utils/severity.js';
import { buildV6ResultDisplay, variantFor } from '../../pdf/helpers/check-block-assembler.js';
import { sentenceCase } from '../../pdf/helpers/check-name-case.js';
import { REPORT_ONLY_CHIP_LABEL } from '../../shared/checkDisplay.js';

/** CSS modifier from the shared label — 'STRUCTURAL FINDING' → 'structural-finding'. */
function chipClassFor(label) {
  return `check-chip check-chip-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
}

/**
 * One check row inside a category's expanded panel. Clicking the header
 * toggles its local expanded state independently from other checks
 * (siblings stay open). Stops propagation so the parent category doesn't
 * collapse when a check is clicked.
 *
 * Reads canonical CheckResult §A1 fields: check_number, check_name,
 * status_threshold, explanation.
 */
export function CheckBlock({ check, reportId }) {
  const [open, setOpen] = useState(false);
  const severity = severityFromCheck(check);
  const cls = severityClass(severity);
  // STAGE4-FIX-B Part 2: the header chip consumes the assembler's display
  // object — chipLabel comes from THE shared §A3 mapping
  // (checkDisplay.statusLabelFor), the same function the PDF result line
  // renders. No local label mapping. `display` is null for plain report-only
  // checks (#5/#14/#17/#19/#25/#27), whose REPORT ONLY chip lives in the
  // expanded body — mirroring the PDF, where those checks carry no result
  // line and chip inside the report-only block instead.
  const display = buildV6ResultDisplay(check);
  // X-4 (V-21): count-carrying report-only checks (#5/#14/#17/#25/#27) return a
  // null display (no result line) yet must show the REPORT ONLY chip in the
  // header top-right — exactly like #21 and the comparison set (#31–34), whose
  // display is non-null. The chip previously lived only in the expanded body, so
  // the collapsed list view showed no status at all (the lone inconsistency).
  // Dashboard-only; the chip string is the shared §A3 constant, and the shared
  // assembler + PDF are untouched (the PDF keeps its report-only-block chip).
  const headerChipLabel = display
    ? display.chipLabel
    : (variantFor(check) === 'reportOnly' ? REPORT_ONLY_CHIP_LABEL : null);
  const panelId = `check-panel-${check.check_number}`;

  return (
    <div className="check-block">
      <div
        className={`check-header check-border-${cls}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((o) => !o);
          }
        }}
      >
        <div className="check-top">
          <div>
            {/* D-DashTitleCase (STAGE4-REVIEW): the check-header title renders
                the shared sentence-case display of the canonical Title-Case
                check_name — byte-identical to the PDF's sentenceCase(checkName)
                title (check-name-case.js is the one implementation). */}
            <span className="check-title">Check #{check.check_number} — {sentenceCase(check.check_name)}</span>
            <span className="check-result">{display?.resultText ?? ''}</span>
            {/* A2 (D-DashThresholdLine): the PDF's Threshold line, mirrored on
                the dashboard header. Sourced from the SAME emission field the
                PDF consumes (threshold_label, surfaced as display.thresholdText
                by the shared buildV6ResultDisplay); the presence rule is the
                PDF's exactly — rendered iff the PDF renders a Threshold line
                (v6Result present with a truthy thresholdText: activity- and
                schedule-level checks plus report-only #21; never the count-only
                report-only checks or the comparison set). */}
            {display?.thresholdText && (
              <span className="check-threshold">Threshold: {display.thresholdText}</span>
            )}
          </div>
          <div className="check-right">
            {headerChipLabel && (
              <span className={chipClassFor(headerChipLabel)}>{headerChipLabel}</span>
            )}
            <span className={`cat-arrow ${open ? 'open' : ''}`} aria-hidden="true">▾</span>
          </div>
        </div>
        {check.explanation && (
          <div className="check-desc">{check.explanation}</div>
        )}
      </div>
      {open && (
        <div id={panelId} className="check-expand open">
          <CheckExpanded check={check} reportId={reportId} />
        </div>
      )}
    </div>
  );
}
